import React, { useState, useEffect } from 'react';
import { useDatabase } from '../contexts/DatabaseContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useTenantTrades } from '../hooks/useTenantTrades';
import pb from '../lib/pocketbase/client';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  ALL_TRADES,
  TRADE_META,
  ACCREDITATIONS,
  ACCREDITATION_FIELDS,
  ROLES,
  ROLE_LABELS,
  ROLE_BADGE_CLASSES
} from '../utils/constants';

const {
  FiSave,
  FiCheckCircle,
  FiBriefcase,
  FiShield,
  FiUsers,
  FiLock,
  FiCheck,
  FiX,
  FiUserPlus,
  FiAlertCircle,
  FiImage,
  FiTrash2,
  FiClock,
  FiMail,
  FiLogOut
} = FiIcons;

const PRESET_COLORS = [
  { name: 'Classic Blue', hex: '#2563eb' },
  { name: 'Emerald Green', hex: '#059669' },
  { name: 'Amber Glow', hex: '#d97706' },
  { name: 'Royal Purple', hex: '#7c3aed' },
  { name: 'Rose Red', hex: '#e11d48' },
  { name: 'Slate Dark', hex: '#334155' }
];

const TABS = [
  { id: 'profile', label: 'Organization Profile', icon: FiBriefcase },
  { id: 'trades', label: 'Trades & Accreditations', icon: FiShield },
  { id: 'team', label: 'Team Members', icon: FiUsers },
  { id: 'security', label: 'Security & Sessions', icon: FiLock }
];

const LICENSE_FIELDS = [
  { key: 'gasSafeRegNumber', accreditation: ACCREDITATIONS.GAS_SAFE },
  { key: 'niceicRegNumber', accreditation: ACCREDITATIONS.NICEIC },
  { key: 'oftecRegNumber', accreditation: ACCREDITATIONS.OFTEC },
  { key: 'napitRegNumber', accreditation: ACCREDITATIONS.NAPIT },
  { key: 'mcsNumber', accreditation: ACCREDITATIONS.MCS }
];

/**
 * SaaS Admin & Tenant Settings (Dev 2 — Sub-Task 2.5)
 * Four-tab admin console: Organization Profile, Trades & Accreditations,
 * Team Members (with invite flow) and Security & Sessions (OTP logs).
 *
 * Persistence is offline-first: local TenantContext + Dexie writes succeed
 * immediately; PocketBase updates are best-effort and never block the UI.
 */
const AdminSettings = () => {
  const { db, isReady } = useDatabase();
  const { activeTenant, updateTenant, updateTenantBranding } = useTenant();
  const { theme, setTheme, tenantPrimaryColor, updateTenantColor } = useTheme();
  const { user, logout, authEvents } = useAuth();
  const { allowedForms } = useTenantTrades();

  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const [error, setError] = useState(null);

  // ---- Tab 1: Organization profile state
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);

  // ---- Tab 2: Trades & accreditations state
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [licenseNumbers, setLicenseNumbers] = useState({
    gasSafeRegNumber: '',
    niceicRegNumber: '',
    oftecRegNumber: '',
    napitRegNumber: '',
    mcsNumber: ''
  });

  // ---- Tab 3: Team state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState(ROLES.ENGINEER);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteNotice, setInviteNotice] = useState(null);

  // Hydrate local state from the active tenant
  useEffect(() => {
    if (!activeTenant) return;
    setCompanyName(activeTenant.name || '');
    setAddress(activeTenant.branding?.companyAddress || '');
    setPhone(activeTenant.branding?.contactPhone || '');
    setEmail(activeTenant.branding?.contactEmail || '');
    setLogoUrl(activeTenant.branding?.logoUrl || null);
    setSelectedTrades(Array.isArray(activeTenant.trades) ? [...activeTenant.trades] : []);
    setLicenseNumbers({
      gasSafeRegNumber: activeTenant.gasSafeRegNumber || '',
      niceicRegNumber: activeTenant.niceicRegNumber || '',
      oftecRegNumber: activeTenant.oftecRegNumber || '',
      napitRegNumber: activeTenant.napitRegNumber || '',
      mcsNumber: activeTenant.mcsNumber || ''
    });
  }, [activeTenant?.id]);

  const flashSaved = () => {
    setSavedNotice(true);
    setTimeout(() => setSavedNotice(false), 2500);
  };

  /** Best-effort remote persistence — offline failures are non-fatal. */
  const syncTenantToPocketBase = async (payload) => {
    try {
      if (!pb.authStore.isValid || !activeTenant?.remote) return;
      await pb.collection('organizations').update(activeTenant.id, payload);
    } catch (e) {
      console.warn('PocketBase tenant sync deferred (offline or unauthorized):', e?.message || e);
    }
  };

  // ---------- Tab 1 handlers ----------
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      setError('Logo must be smaller than 512 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoUrl(reader.result);
      updateTenantBranding({ logoUrl: reader.result });
      flashSaved();
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
    updateTenantBranding({ logoUrl: null });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);
    try {
      updateTenant({ name: companyName.trim() });
      updateTenantBranding({
        companyAddress: address,
        contactPhone: phone,
        contactEmail: email,
        primaryColor: tenantPrimaryColor,
        logoUrl
      });
      if (isReady) {
        await db.settings.put({ key: 'companyName', value: companyName.trim(), updatedAt: new Date() });
      }
      await syncTenantToPocketBase({
        name: companyName.trim(),
        branding: {
          ...(activeTenant.branding || {}),
          companyAddress: address,
          contactPhone: phone,
          contactEmail: email,
          primaryColor: tenantPrimaryColor,
          logoUrl
        }
      });
      flashSaved();
    } catch (e) {
      console.error('Failed to save organization profile:', e);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Tab 2 handlers ----------
  const toggleTrade = async (trade) => {
    const next = selectedTrades.includes(trade)
      ? selectedTrades.filter((t) => t !== trade)
      : [...selectedTrades, trade];
    if (next.length === 0) {
      setError('At least one trade sector must remain enabled.');
      return;
    }
    setError(null);
    setSelectedTrades(next);
    updateTenant({ trades: next });
    await syncTenantToPocketBase({ trades: next });
    flashSaved();
  };

  const toggleAccreditation = async (accreditation) => {
    const current = activeTenant.accreditations || [];
    const next = current.includes(accreditation)
      ? current.filter((a) => a !== accreditation)
      : [...current, accreditation];
    updateTenant({ accreditations: next });
    await syncTenantToPocketBase({ accreditations: next });
    flashSaved();
  };

  const handleLicenseChange = (key, value) => {
    setLicenseNumbers((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveAccreditations = async () => {
    setSaving(true);
    try {
      // Sync accreditation toggles with any license numbers entered
      let accreditations = [...(activeTenant.accreditations || [])];
      LICENSE_FIELDS.forEach(({ key, accreditation }) => {
        const hasNumber = Boolean(licenseNumbers[key]?.trim());
        if (hasNumber && !accreditations.includes(accreditation)) {
          accreditations.push(accreditation);
        }
      });
      updateTenant({
        accreditations,
        gasSafeRegNumber: licenseNumbers.gasSafeRegNumber.trim(),
        niceicRegNumber: licenseNumbers.niceicRegNumber.trim(),
        oftecRegNumber: licenseNumbers.oftecRegNumber.trim(),
        napitRegNumber: licenseNumbers.napitRegNumber.trim(),
        mcsNumber: licenseNumbers.mcsNumber.trim()
      });
      if (isReady) {
        await db.settings.put({ key: 'gasSafeRegNumber', value: licenseNumbers.gasSafeRegNumber.trim(), updatedAt: new Date() });
      }
      await syncTenantToPocketBase({
        accreditations,
        gasSafeRegNumber: licenseNumbers.gasSafeRegNumber.trim(),
        niceicRegNumber: licenseNumbers.niceicRegNumber.trim(),
        oftecRegNumber: licenseNumbers.oftecRegNumber.trim(),
        napitRegNumber: licenseNumbers.napitRegNumber.trim(),
        mcsNumber: licenseNumbers.mcsNumber.trim()
      });
      flashSaved();
    } catch (e) {
      console.error('Failed to save accreditations:', e);
      setError('Failed to save accreditations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Tab 3 handlers ----------
  const handleInvite = async () => {
    const trimmedEmail = inviteEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setInviteNotice({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    const members = activeTenant.members || [];
    if (members.some((m) => m.email === trimmedEmail)) {
      setInviteNotice({ type: 'error', message: 'This email is already a team member.' });
      return;
    }

    setInviteSending(true);
    try {
      // Best-effort remote invite (PocketBase user creation); local roster
      // updates immediately so the flow works fully offline.
      let remoteId = null;
      try {
        if (pb.authStore.isValid) {
          const randomPassword = crypto?.randomUUID
            ? crypto.randomUUID().replace(/-/g, '')
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const created = await pb.collection('users').create({
            email: trimmedEmail,
            name: inviteName.trim() || trimmedEmail.split('@')[0],
            password: randomPassword,
            passwordConfirm: randomPassword,
            organization_id: activeTenant.remote ? activeTenant.id : undefined,
            role: ROLE_LABELS[inviteRole],
            active: true,
            otpEnabled: true
          });
          remoteId = created?.id || null;
        }
      } catch (e) {
        console.warn('Remote invite deferred (offline or unauthorized):', e?.message || e);
      }

      const member = {
        id: remoteId || `usr_local_${Date.now().toString(36)}`,
        name: inviteName.trim() || trimmedEmail.split('@')[0],
        email: trimmedEmail,
        role: inviteRole,
        active: true,
        invitedAt: new Date().toISOString()
      };
      updateTenant({ members: [...members, member] });

      // Member signs in via the same email OTP flow — the emailed code IS the invite.
      setInviteNotice({
        type: 'success',
        message: `Invitation sent to ${trimmedEmail}. They can sign in immediately via email OTP.`
      });
      setInviteEmail('');
      setInviteName('');
      setInviteRole(ROLES.ENGINEER);
      setTimeout(() => {
        setInviteOpen(false);
        setInviteNotice(null);
      }, 2600);
    } finally {
      setInviteSending(false);
    }
  };

  const handleRoleChange = async (memberId, role) => {
    const members = (activeTenant.members || []).map((m) =>
      m.id === memberId ? { ...m, role } : m
    );
    updateTenant({ members });
    try {
      if (pb.authStore.isValid && !memberId.startsWith('usr_local_')) {
        await pb.collection('users').update(memberId, { role: ROLE_LABELS[role] });
      }
    } catch (e) {
      console.warn('Remote role update deferred:', e?.message || e);
    }
    flashSaved();
  };

  const handleRemoveMember = async (memberId) => {
    const members = (activeTenant.members || []).filter((m) => m.id !== memberId);
    updateTenant({ members });
    try {
      if (pb.authStore.isValid && !memberId.startsWith('usr_local_')) {
        await pb.collection('users').delete(memberId);
      }
    } catch (e) {
      console.warn('Remote member removal deferred:', e?.message || e);
    }
    flashSaved();
  };

  // ---------- Render helpers ----------
  const inputClass =
    'w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500';

  const members = activeTenant.members || [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
            Organization Administration
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {activeTenant.name} • {activeTenant.plan} plan • {allowedForms.length} certificate types enabled
          </p>
        </div>
        {savedNotice && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
            <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />
            Changes saved
          </span>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 flex items-start gap-2.5">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1.5 overflow-x-auto border-b border-slate-200 dark:border-slate-800 pb-px">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold rounded-t-lg whitespace-nowrap transition-all border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 bg-blue-50/60 dark:bg-blue-950/30'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <SafeIcon icon={tab.icon} className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ================= TAB 1: Organization Profile ================= */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company details */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
              <SafeIcon icon={FiBriefcase} className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Company Details
              </h3>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Organization Name</label>
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Business Address</label>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Phone</label>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Contact Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
              </div>
            </div>

            {/* Logo upload */}
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">Brand Logo</label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain" />
                  ) : (
                    <SafeIcon icon={FiImage} className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-bold cursor-pointer transition-colors">
                    Upload Logo
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                  </label>
                  {logoUrl && (
                    <button
                      onClick={handleRemoveLogo}
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg text-xs font-bold transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-md transition-all disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Organization Profile'}
            </button>
          </div>

          {/* Appearance */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
              <SafeIcon icon={FiLock} className="w-4 h-4 text-purple-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Brand Color & Appearance
              </h3>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">Interface Theme</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'light', label: 'Light' },
                  { id: 'dark', label: 'Dark' },
                  { id: 'system', label: 'System' }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTheme(opt.id)}
                    className={`p-2.5 rounded-xl border text-xs font-semibold transition-all ${
                      theme === opt.id
                        ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                        : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2">Tenant Brand Accent</label>
              <div className="flex items-center gap-2 mb-3">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => updateTenantColor(color.hex)}
                    className={`w-7 h-7 rounded-full transition-transform ${
                      tenantPrimaryColor === color.hex ? 'ring-2 ring-offset-2 ring-slate-800 dark:ring-white scale-110' : ''
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={tenantPrimaryColor}
                  onChange={(e) => updateTenantColor(e.target.value)}
                  className="w-8 h-8 rounded border border-slate-300 dark:border-slate-700 cursor-pointer p-0 bg-transparent"
                />
                <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{tenantPrimaryColor}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= TAB 2: Trades & Accreditations ================= */}
      {activeTab === 'trades' && (
        <div className="space-y-6">
          {/* Trade toggles */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-emerald-500" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                  Trade Sectors
                </h3>
              </div>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {selectedTrades.length} of {ALL_TRADES.length} enabled
              </span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
              Toggling a trade immediately filters which statutory certificates appear in the Form
              Library, Dashboard and Command Palette for every member of this organization.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {ALL_TRADES.map((trade) => {
                const meta = TRADE_META[trade];
                const enabled = selectedTrades.includes(trade);
                return (
                  <button
                    key={trade}
                    type="button"
                    onClick={() => toggleTrade(trade)}
                    className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                      enabled
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <span className="text-2xl leading-none mt-0.5">{meta.icon}</span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-xs font-bold ${enabled ? 'text-emerald-800 dark:text-emerald-200' : 'text-slate-900 dark:text-slate-100'}`}>
                        {meta.label}
                      </span>
                      <span className="block text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{meta.description}</span>
                    </span>
                    <span
                      className={`w-9 h-5 rounded-full flex items-center px-0.5 flex-shrink-0 mt-0.5 transition-colors ${
                        enabled ? 'bg-emerald-500 justify-end' : 'bg-slate-300 dark:bg-slate-700 justify-start'
                      }`}
                    >
                      <span className="w-4 h-4 rounded-full bg-white shadow-sm" />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Accreditation license numbers */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Accreditation & License Numbers
              </h3>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
              These statutory registration numbers are printed on every issued certificate PDF.
              Entering a number automatically enables its accreditation badge.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {LICENSE_FIELDS.map(({ key, accreditation }) => {
                const meta = ACCREDITATION_FIELDS[accreditation];
                const active = (activeTenant.accreditations || []).includes(accreditation);
                return (
                  <div key={key} className={`p-3 rounded-xl border transition-colors ${active ? 'border-emerald-300 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-slate-200 dark:border-slate-700'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">{meta.label}</label>
                      <button
                        type="button"
                        onClick={() => toggleAccreditation(accreditation)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          active ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 dark:border-slate-600'
                        }`}
                        title={active ? 'Accreditation enabled' : 'Accreditation disabled'}
                      >
                        {active && <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 text-white" />}
                      </button>
                    </div>
                    <input
                      type="text"
                      value={licenseNumbers[key]}
                      onChange={(e) => handleLicenseChange(key, e.target.value)}
                      placeholder={meta.placeholder}
                      className={inputClass}
                    />
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleSaveAccreditations}
              disabled={saving}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-md transition-all disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Accreditations'}
            </button>
          </div>
        </div>
      )}

      {/* ================= TAB 3: Team Members ================= */}
      {activeTab === 'team' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiUsers} className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Team Members ({members.length})
              </h3>
            </div>
            <button
              onClick={() => {
                setInviteOpen(true);
                setInviteNotice(null);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold shadow-sm transition-all"
            >
              <SafeIcon icon={FiUserPlus} className="w-3.5 h-3.5" />
              Invite Member
            </button>
          </div>

          {/* Invite modal */}
          {inviteOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
              <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-black text-slate-900 dark:text-slate-100">Invite Team Member</h3>
                  <button
                    onClick={() => setInviteOpen(false)}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                  Members sign in with email OTP — no password to set. The emailed single-use code
                  acts as their invitation.
                </p>

                {inviteNotice && (
                  <div className={`p-3 rounded-xl border flex items-start gap-2 ${inviteNotice.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900' : 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900'}`}>
                    <SafeIcon
                      icon={inviteNotice.type === 'success' ? FiCheckCircle : FiAlertCircle}
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${inviteNotice.type === 'success' ? 'text-emerald-500' : 'text-red-500'}`}
                    />
                    <p className={`text-xs ${inviteNotice.type === 'success' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                      {inviteNotice.message}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Email Address</label>
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="engineer@company.co.uk"
                        className={`${inputClass} pl-9`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Full Name <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="Tom Bradley"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Role</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[ROLES.OWNER, ROLES.ADMIN, ROLES.ENGINEER].map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setInviteRole(role)}
                          className={`p-2.5 rounded-xl border text-xs font-bold transition-all ${
                            inviteRole === role
                              ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                              : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                          }`}
                        >
                          {ROLE_LABELS[role]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2.5 pt-2">
                  <button
                    onClick={() => setInviteOpen(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInvite}
                    disabled={inviteSending || !inviteEmail.trim()}
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold shadow-md transition-all disabled:opacity-50"
                  >
                    {inviteSending ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Member list */}
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {members.length === 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400 py-6 text-center">
                No members yet — invite your first engineer to get started.
              </p>
            )}
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-slate-700 to-slate-800 dark:from-blue-600 dark:to-blue-500 text-white flex items-center justify-center text-[11px] font-black flex-shrink-0">
                    {member.name
                      ?.split(' ')
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">{member.name}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                    className={`text-[10px] font-bold px-2 py-1 rounded-full border outline-none cursor-pointer ${ROLE_BADGE_CLASSES[member.role] || ROLE_BADGE_CLASSES[ROLES.ENGINEER]}`}
                  >
                    <option value={ROLES.OWNER}>{ROLE_LABELS[ROLES.OWNER]}</option>
                    <option value={ROLES.ADMIN}>{ROLE_LABELS[ROLES.ADMIN]}</option>
                    <option value={ROLES.ENGINEER}>{ROLE_LABELS[ROLES.ENGINEER]}</option>
                  </select>
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                    title="Remove member"
                  >
                    <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= TAB 4: Security & Sessions ================= */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Current session */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
              <SafeIcon icon={FiLock} className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Current Session
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-slate-500 block text-[11px]">Signed in as</span>
                <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                  {user?.name || user?.email || 'Local Workspace'}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block text-[11px]">Auth method</span>
                <span className="font-bold text-slate-800 dark:text-slate-200">Email OTP (JWT)</span>
              </div>
              <div>
                <span className="text-slate-500 block text-[11px]">Token status</span>
                <span className={`font-bold ${user ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {user ? 'Valid session' : 'Offline / no session'}
                </span>
              </div>
            </div>
            <button
              onClick={() => logout()}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-bold shadow-md transition-all"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              Sign Out of All Sessions
            </button>
          </div>

          {/* OTP verification log */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-3">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
              <SafeIcon icon={FiClock} className="w-4 h-4 text-purple-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                OTP Verification Log
              </h3>
            </div>
            {authEvents.length === 0 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400 py-4 text-center">
                No authentication events recorded on this device yet.
              </p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto">
                {authEvents.map((evt) => (
                  <div key={evt.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                        {evt.action.replace(/_/g, ' ')}
                      </p>
                      {evt.detail && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-xs">{evt.detail}</p>
                      )}
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">
                      {new Date(evt.timestamp).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
